// Hebrew string catalogue — keyed by string id
// (docs/design/14-ui-component-spec.md §4). Error codes come from
// docs/design/10-signaling-protocol.md §5.

export const he = {
  // Generic
  app: {
    // Brand name is the same English name in every locale.
    name: "Whisperz Secured Conferencing Systems",
    brand: "Whisperz Secured Conferencing Systems",
    startupError: "אירעה תקלה בטעינת המערכת — נא לרענן או לפנות לתמיכה."
  },
  nav: {
    home: "בית",
    schedule: "פגישות",
    admin: "ניהול",
    switchLanguage: "החלפת שפה",
    placeholder: "מסך זה ייבנה בשלבי הפיתוח הבאים. ראו"
  },
  // Status / banner
  banner: {
    reconnecting: "מתחבר מחדש…",
    recordingOn: "מוקלט"
  },
  // Embedded launch context (modules/04 §3)
  launch: {
    caseContext: "פרטי תיק",
    driver: "ת״ז נהג",
    caseRef: "אסמכתא",
    system: "מערכת מקור"
  },
  // Call flow (docs/design/05-uiux-spec.md §3)
  call: {
    precallTitle: "בחירת קצין לשימוע",
    search: "חיפוש קצין…",
    available: "זמין",
    unavailable: "לא זמין",
    callAction: "התקשר",
    noOfficers: "אין קצינים זמינים כעת.",
    callingTitle: "מתקשר…",
    cancel: "ביטול",
    incomingTitle: "שיחה נכנסת",
    accept: "ענה",
    decline: "דחה",
    endCall: "סיים שיחה",
    endedTitle: "השיחה הסתיימה",
    backToList: "חזרה לרשימה",
    mute: "השתק",
    unmute: "בטל השתקה",
    camOff: "כבה מצלמה",
    camOn: "הפעל מצלמה",
    selfPanel: "השוטר והאזרח",
    officerPanel: "הקצין",
    availabilityToggle: "סטטוס זמינות",
    shareScreen: "שיתוף מסך",
    stopShare: "הפסק שיתוף",
    screenSharing: "משתף מסך",
    controlsLabel: "בקרת שיחה",
    fullscreen: "מסך מלא",
    exitFullscreen: "יציאה ממסך מלא",
    officerStation: "עמדת קצין",
    waitingTitle: "ממתין לשיחה נכנסת",
    waitingStation: "עמדה זו פנויה וזמינה לקבלת שיחות נכנסות.",
    localPreview: "תצוגה מקומית",
    tokenExpired: "פג תוקף החיבור. יש לפתוח את קישור ההדגמה מחדש.",
    connectFailed: "לא ניתן להתחבר לשירות השיחות. יש לרענן את הדף או לפתוח את קישור ההדגמה מחדש.",
    missingCallId: "לא נמצא מזהה שיחה לפתיחת ההתקשרות.",
    hubNotReady: "שירות השיחות עדיין לא מחובר. יש לרענן את הדף או לפתוח את קישור ההדגמה מחדש.",
    startFailed: "לא ניתן להתחיל את השיחה.",
    callerFallback: "שוטר"
  },
  // Device readiness ("get ready before the call") — design/05
  readiness: {
    title: "בדיקת מצלמה ומיקרופון",
    subtitle: "יש לוודא שהמצלמה והמיקרופון פועלים לפני תחילת השיחה.",
    camera: "מצלמה",
    microphone: "מיקרופון",
    cameraOn: "מצלמה פועלת",
    cameraOff: "מצלמה כבויה",
    micOn: "מיקרופון פועל",
    micOff: "מיקרופון מושתק",
    cameraOffPreview: "המצלמה כבויה",
    continueTraffic: "מוכן/ה — התחל לקבל שיחות",
    continueField: "המשך לבחירת קצין",
    retry: "נסה שוב",
    requesting: "מבקש גישה למצלמה ולמיקרופון…",
    noDevice: "לא נמצאו מצלמה או מיקרופון זמינים — יש לחבר התקן ולנסות שוב.",
    trackEnded: "המצלמה או המיקרופון הפסיקו לפעול. יש לבדוק את ההתקן ולנסות שוב.",
    autoChecking: "בודק את המצלמה והמיקרופון. ההצטרפות תתבצע אוטומטית.",
    autoJoining: "המצלמה והמיקרופון פועלים. מצטרף/ת…",
    autoWaiting: "ממתין למצלמה ולמיקרופון פעילים. יש לוודא ששניהם מופעלים ואינם בשימוש באפליקציה אחרת."
  },
  // Availability (preference 2)
  availability: {
    available: "זמין",
    unavailable: "לא זמין",
    title: "סטטוס זמינות"
  },
  // Scheduling (preference 1)
  schedule: {
    title: "פגישות מתוזמנות",
    none: "אין פגישות מתוזמנות.",
    join: "הצטרף",
    cancel: "בטל",
    start: "מועד",
    duration: "משך (דק׳)",
    caseRef: "אסמכתא"
  },
  // Admin console (design/05 §6)
  admin: {
    title: "מסוף ניהול",
    officers: "קצינים",
    recordings: "הקלטות",
    dashboard: "לוח בקרה",
    config: "תצורה",
    settings: "הגדרות",
    // Shown when the admin APIs return 401/403 — no admin-authorized session is present.
    notAuthorized: "נדרשת הרשאת ניהול לצפייה במסוף זה. יש להיכנס עם חשבון מנהל או צופה IT.",
    sessionExpired: "ההתחברות פגה. יש להיכנס מחדש."
  },
  // Administrator sign-in protecting /admin and /schedule
  auth: {
    title: "כניסת מנהל מערכת",
    hint: "יש להיכנס עם חשבון מנהל המערכת כדי לנהל את המערכת ואת הפגישות המתוזמנות.",
    username: "שם משתמש",
    password: "סיסמה",
    submit: "כניסה",
    checking: "מתחבר…",
    required: "יש להזין שם משתמש וסיסמה.",
    failed: "שם המשתמש או הסיסמה שגויים.",
    locked: "בוצעו יותר מדי ניסיונות כושלים. יש להמתין מספר דקות ולנסות שוב.",
    unavailable: "הכניסה אינה זמינה כרגע. יש לנסות שוב או לפנות לתמיכה.",
    signOut: "יציאה",
    signedInAs: "מחובר/ת בתור",
    sessionExpired: "פג תוקף ההתחברות. יש להיכנס מחדש.",
    defaultPasswordWarning: "התחברת עם סיסמת ברירת המחדל. יש להחליף אותה כעת.",
    goToSettings: "החלפת סיסמה"
  },
  // Admin settings — password change
  settings: {
    passwordTitle: "החלפת סיסמת מנהל המערכת",
    passwordHint: "יש להשתמש ב־10 עד 128 תווים. החלפת הסיסמה מנתקת את כל חיבורי המנהל האחרים.",
    current: "סיסמה נוכחית",
    next: "סיסמה חדשה",
    confirm: "אימות סיסמה חדשה",
    submit: "החלפת סיסמה",
    saving: "שומר…",
    success: "הסיסמה הוחלפה בהצלחה.",
    errors: {
      currentRequired: "יש להזין את הסיסמה הנוכחית.",
      currentIncorrect: "הסיסמה הנוכחית שגויה.",
      mismatch: "הסיסמה החדשה ואימות הסיסמה אינם תואמים.",
      length: "הסיסמה החדשה חייבת להכיל 10 עד 128 תווים.",
      notAllowed: "הסיסמה החדשה חייבת להיות שונה מהסיסמה הנוכחית, מסיסמת ברירת המחדל ומשם המשתמש.",
      locked: "בוצעו יותר מדי ניסיונות כושלים. יש להמתין מספר דקות ולנסות שוב."
    }
  },
  // Public phone/OTP → two-party call flow (site root "/" and guest link "/join")
  pub: {
    loading: "טוען…",
    welcomeTitle: "ברוכים הבאים",
    welcomeText: "יש להזין את מספר הטלפון כדי להתחיל שיחת וידאו מאובטחת.",
    phoneLabel: "מספר טלפון",
    phonePlaceholder: "+1-868-1234567",
    continue: "המשך",
    checking: "בודק…",
    otpTitle: "הזנת קוד אימות",
    otpTextWhatsApp: "שלחנו קוד אימות בן 6 ספרות ל־WhatsApp שלך. יש להזין אותו כדי להמשיך:",
    otpTextMock: "מצב בדיקה: יש להזין את קוד האימות שקיבלת לצורך הבדיקה. טלפון:",
    otpLabel: "קוד אימות",
    verify: "אימות",
    changePhone: "שימוש במספר טלפון אחר",
    guestTitle: "למי להתקשר?",
    guestText: "יש להזין את מספר הטלפון של המשתתף השני. אין צורך שיהיה רשום במערכת.",
    guestLabel: "מספר הטלפון של המשתתף השני",
    guestContinue: "המשך לבדיקת התקנים",
    signedInAs: "מחובר/ת בתור",
    readyAction: "מוכן/ה",
    preparing: "מכין את ההזמנה…",
    waitingTitle: "ממתין למשתתף השני",
    waitingText: "השיחה תתחיל אוטומטית ברגע שהמשתתף השני יהיה מוכן.",
    calling: "מתקשר אל",
    guestStates: {
      notOpened: "ההזמנה עדיין לא נפתחה.",
      joining: "המשתתף השני פתח את ההזמנה ובודק את ההתקנים.",
      ready: "המשתתף השני מוכן. השיחה מתחילה…",
      away: "המשתתף השני עזב את הדף. ניתן לפתוח שוב את קישור ההזמנה."
    },
    delivery: {
      "mock-not-sent": "מצב בדיקה: משלוח WhatsApp כבוי ולא נשלחה הודעה. יש להעתיק את קישור ההזמנה ולשתף אותו עם המשתתף השני.",
      "waha-sending": "שליחת ההזמנה ב־WhatsApp לא אושרה. היא לא תישלח שוב אוטומטית — יש להעתיק את הקישור אם המשתתף השני לא קיבל אותו.",
      "waha-accepted": "ההזמנה התקבלה בשירות WhatsApp למשלוח למשתתף השני. אין בכך אישור שהתקבלה — ניתן גם להעתיק את הקישור.",
      "waha-failed": "לא ניתן היה לשלוח את ההזמנה ב־WhatsApp. יש להעתיק את הקישור ולשתף אותו בדרך אחרת.",
      "waha-unknown": "שירות WhatsApp לא אישר את ההזמנה. היא לא נשלחה שוב אוטומטית — יש להעתיק את הקישור אם המשתתף השני לא קיבל אותו.",
      "delivery-unavailable": "משלוח WhatsApp אינו זמין כרגע ולכן לא נשלחה הודעה. יש להעתיק את הקישור ולשתף אותו בדרך אחרת.",
      pending: "מכין את ההזמנה…"
    } as Record<string, string>,
    copyLink: "העתקת קישור ההזמנה",
    copied: "קישור ההזמנה הועתק.",
    copyFailed: "לא ניתן היה להעתיק את הקישור אוטומטית. הדפדפן חסם גישה ללוח.",
    inviteExpires: "ההזמנה בתוקף עד",
    cancel: "ביטול השיחה",
    startOver: "התחלה מחדש",
    starting: "השיחה מתחילה…",
    guestTitleInvited: "הוזמנת לשיחת וידאו",
    invitedBy: "הזמנה מאת",
    guestText2: "יש לאשר גישה למצלמה ולמיקרופון. ההצטרפות תתבצע אוטומטית ברגע ששניהם פועלים.",
    guestTextManual: "יש לבדוק את המצלמה והמיקרופון ולבחור ״מוכן/ה״.",
    guestWaitingTitle: "ממתין לתחילת השיחה",
    guestWaitingText: "השיחה תתחיל אוטומטית.",
    initiatorAway: "מי שהזמין אותך אינו מחובר כרגע. נא להשאיר דף זה פתוח.",
    leave: "יציאה",
    endedTitle: "השיחה הסתיימה",
    endedText: "תודה. ניתן לסגור דף זה.",
    closedTitle: "השיחה אינה זמינה",
    closed: {
      cancelled: "השיחה בוטלה.",
      expired: "תוקף ההזמנה פג.",
      invalid: "קישור ההזמנה אינו תקין או שפג תוקפו.",
      signedOut: "ההתחברות הסתיימה. יש להתחיל מחדש."
    },
    errors: {
      invalidPhone: "יש להזין מספר טלפון תקין.",
      cannotContinue: "לא ניתן להמשיך עם מספר טלפון זה. יש לבדוק את המספר או לפנות למנהל המערכת.",
      tooMany: "בוצעו יותר מדי ניסיונות. יש להמתין מספר דקות ולנסות שוב.",
      codeRejected: "הקוד שגוי או שפג תוקפו.",
      codeRequired: "יש להזין את קוד האימות.",
      sameParty: "יש להזין מספר טלפון שונה מהמספר שלך.",
      unavailable: "השירות אינו זמין כרגע. יש לנסות שוב.",
      deliveryUnavailable: "לא ניתן היה לשלוח את קוד האימות כרגע. יש לנסות שוב מאוחר יותר.",
      generic: "אירעה תקלה. יש לנסות שוב."
    }
  },
  // Admin settings — registered users of the public flow
  // Admin settings — WhatsApp (WAHA) delivery
  waha: {
    title: "משלוח WhatsApp ‏(WAHA)",
    loading: "טוען הגדרות WhatsApp…",
    activeTitle: "משלוח WhatsApp פעיל.",
    activeText: "קודי כניסה והזמנות לשיחה חדשים נשלחים דרך WhatsApp. כיבוי מחזיר כניסות חדשות למצב בדיקה.",
    offTitle: "משלוח WhatsApp כבוי.",
    offText: "הכניסה פועלת במצב בדיקה וההזמנות אינן נשלחות; היוזם מעתיק את הקישור. ההגדרות השמורות נשארות מוצפנות.",
    stateEmpty: "טרם הוגדר.",
    stateDraft: "נשמר כטיוטה — נדרשים גם כתובת השירות וגם מפתח ה־API להשלמה.",
    stateComplete: "ההגדרה הושלמה.",
    baseUrl: "כתובת השירות (HTTPS)",
    baseUrlHint: "כתובת שירות WAHA, למשל https://waha.example.internal — https בלבד, ללא שם משתמש, שאילתה או מקטע. כתובות מקומיות ו־link-local אינן מותרות.",
    session: "שם ה־session",
    apiKey: "מפתח API",
    apiKeyConfigured: "מוגדר — השאירו ריק כדי לשמור",
    apiKeyEmpty: "הזינו את מפתח ה־API",
    apiKeyKeepHint: "כתיבה בלבד. השאירו ריק כדי לשמור את המפתח הקיים, או הקלידו מפתח חדש להחלפה.",
    apiKeyWriteOnlyHint: "כתיבה בלבד. המפתח נשמר מוצפן ולא יוצג שוב.",
    clearApiKey: "הסרת מפתח ה־API השמור",
    clearNeedsDisable: "כדי להסיר את המפתח יש קודם לכבות את משלוח WhatsApp ולשמור.",
    testRecipient: "טלפון נמען בדיקה",
    testRecipientHint: "\"שליחת הודעת בדיקה\" שלמטה שולחת רק למספר השמור הזה. השמירה אינה שולחת דבר.",
    enable: "הפעלת משלוח WhatsApp",
    enableHint: "כשהאפשרות פעילה, כל קוד כניסה חדש וכל הזמנה חדשה לשיחה נשלחים כהודעת WhatsApp. נדרשת הגדרה מלאה.",
    save: "שמירת הגדרות",
    saving: "שומר…",
    savedActive: "ההגדרות נשמרו (מוצפנות). משלוח WhatsApp פעיל.",
    savedOff: "ההגדרות נשמרו (מוצפנות). משלוח WhatsApp כבוי.",
    checkTitle: "בדיקת חיבור",
    checkHint: "בודק מול שירות WAHA אם ה־session השמור מוכן. לא נשלחת הודעה.",
    check: "בדיקת חיבור",
    checking: "בודק…",
    actionsNeedComplete: "יש לשמור הגדרה מלאה (כתובת שירות ומפתח API) כדי להשתמש בפעולות אלה.",
    actionsNeedSave: "יש לשמור קודם את השינויים — הפעולות משתמשות בהגדרות השמורות.",
    checkResults: {
      working: "מחובר: ה־session של WhatsApp מוכן (סטטוס WORKING).",
      "session-not-working": "מחובר, אך ה־session של WhatsApp אינו מוכן. יש לפתוח את לוח WAHA כדי להפעיל או לצמד אותו.",
      unauthorized: "שירות WAHA דחה את מפתח ה־API.",
      "session-not-found": "בשירות WAHA אין session בשם זה.",
      unreachable: "לא ניתן להגיע לשירות WAHA (בעיית רשת או תעודה).",
      timeout: "שירות WAHA לא ענה בזמן.",
      "blocked-target": "כתובת השירות מתורגמת לכתובת רשת חסומה.",
      failed: "שירות WAHA החזיר תשובה לא צפויה."
    },
    testTitle: "שליחת הודעת בדיקה",
    testWarning: "פעולה זו שולחת הודעת WhatsApp אמיתית אחת (טקסט בדיקה קבוע של Whisperz) לנמען הבדיקה השמור בלבד. היא פועלת גם כשהמשלוח כבוי ולעולם אינה נשלחת אוטומטית.",
    testTarget: "נשלח לנמען הבדיקה השמור:",
    testTargetNone: "לא נשמר",
    testNeedsRecipient: "יש לשמור למעלה טלפון נמען בדיקה כדי לשלוח הודעת בדיקה.",
    sendTest: "שליחת הודעת בדיקה",
    sendingTest: "שולח…",
    testResults: {
      accepted: "ההודעה התקבלה בשירות WAHA למשלוח. יש לבדוק בטלפון — הקבלה אינה מאשרת שההודעה הגיעה.",
      rejected: "שירות WAHA לא שלח את ההודעה.",
      unknown: "לא התקבל אישור משירות WAHA. ייתכן שההודעה נשלחה וייתכן שלא; היא לא נשלחה שוב."
    },
    errors: {
      urlInvalid: "הזינו כתובת שירות https:// ללא שם משתמש, שאילתה או מקטע.",
      urlTarget: "כתובת שירות זו אינה מותרת (מקומית, link-local או שמורה).",
      sessionInvalid: "שם ה־session חייב להכיל 1–64 אותיות, ספרות, '-' או '_'.",
      apiKeyInvalid: "מפתח ה־API חייב להכיל 1–512 תווים מודפסים ללא מעברי שורה.",
      recipientInvalid: "הזינו מספר טלפון תקין.",
      clearConflict: "יש להזין מפתח API חדש או להסיר את הקיים — לא את שניהם.",
      enableIncomplete: "ניתן להפעיל את משלוח WhatsApp רק עם כתובת שירות, session ומפתח API.",
      disableFirst: "יש לכבות את משלוח WhatsApp לפני הסרת כתובת השירות או מפתח ה־API.",
      incomplete: "יש לשמור קודם הגדרה מלאה.",
      recipientRequired: "יש לשמור קודם טלפון נמען בדיקה.",
      tooMany: "בוצעו יותר מדי ניסיונות. יש להמתין מספר דקות ולנסות שוב.",
      tooManyTests: "נשלחו יותר מדי הודעות בדיקה. יש להמתין לפני שליחה נוספת.",
      httpsRequired: "ניתן להשתמש בהגדרות אלה רק בחיבור מאובטח (HTTPS).",
      unavailable: "אחסון ההגדרות המאובטח אינו זמין. יש לפנות למנהל המערכת לבדיקת המפתח והאחסון בשרת.",
      generic: "לא ניתן היה להשלים את הפעולה. יש לנסות שוב."
    }
  },  regUsers: {
    title: "משתמשים רשומים",
    hint: "רק משתמשים רשומים פעילים יכולים להתחיל שיחה מהאתר הציבורי.",
    phone: "מספר טלפון",
    name: "שם לתצוגה",
    status: "סטטוס",
    actions: "פעולות",
    active: "פעיל",
    disabled: "מושבת",
    add: "הוספת משתמש",
    save: "שמירה",
    cancel: "ביטול",
    edit: "עריכה",
    enable: "הפעלה",
    disable: "השבתה",
    remove: "הסרה",
    removeConfirm: "להסיר משתמש זה? משתמש עם היסטוריית שיחות יושבת במקום להימחק.",
    removed: "המשתמש הוסר.",
    deactivated: "למשתמש יש היסטוריית שיחות ולכן הוא הושבת במקום להימחק.",
    saved: "נשמר.",
    none: "אין עדיין משתמשים רשומים.",
    loading: "טוען משתמשים רשומים…",
    errors: {
      invalidPhone: "יש להזין מספר טלפון תקין.",
      nameRequired: "יש להזין שם לתצוגה (עד 128 תווים).",
      duplicate: "כבר קיים משתמש רשום עם מספר טלפון זה.",
      notFound: "המשתמש אינו קיים עוד. יש לרענן את הרשימה.",
      generic: "לא ניתן היה לשמור את השינוי. יש לנסות שוב."
    }
  },
  // Admin operational dashboard (design/05 §6)
  dash: {
    subtitle: "מצב תפעולי של מערכת השימוע",
    updated: "עודכן",
    refresh: "רענון",
    sectionStatus: "מצב תפעולי",
    sectionHealth: "תקינות שירותים",
    sectionRecordings: "סטטוס הקלטות",
    sectionOfficers: "זמינות קצינים",
    sectionActivity: "פעילות אחרונה",
    sectionMetrics: "מדדי תשתית וניטור",
    officersAvailable: "קצינים זמינים",
    officersUnavailable: "לא זמינים",
    recInProgress: "הקלטות בעיבוד",
    recCompleted: "הקלטות שהושלמו",
    recFailed: "הקלטות שנכשלו",
    recQueued: "בתור",
    recSigned: "חתומות דיגיטלית",
    storageUsed: "נפח אחסון",
    totalMinutes: "דקות הקלטה",
    latestRecording: "הקלטה אחרונה",
    of: "מתוך",
    serviceUp: "פעיל",
    serviceDown: "לא זמין",
    metricsNotReported: "מדדי התשתית (ניטור) טרם דווחו. הנתונים התפעוליים למעלה משקפים את מצב המערכת בפועל.",
    noActivity: "אין פעילות הקלטות אחרונה.",
    secondsUnit: "שנ׳",
    // Service display names (raw health keys → Hebrew); unknown keys fall back to the raw name.
    services: {
      "app-service": "שירות יישום",
      "media-service": "שירות מדיה",
      "compositing-worker": "עיבוד הקלטות"
    } as Record<string, string>,
    // Hebrew labels for the raw KPI names (shown only when a KPI is non-zero).
    kpi: {
      vc_active_calls: "שיחות פעילות",
      vc_configured_capacity: "קיבולת מוגדרת",
      vc_available_slots: "עמדות פנויות",
      vc_capacity_utilization_ratio: "ניצולת קיבולת",
      vc_call_setup_seconds_p95: "זמן חיבור (p95)",
      vc_call_failure_ratio: "שיעור כשל שיחות",
      vc_reconnect_ratio: "שיעור חיבור מחדש",
      vc_compositing_queue_depth: "תור עיבוד הקלטות",
      vc_compositing_latency_seconds_p95: "זמן עיבוד (p95)",
      vc_compositing_failed_total: "כשלי עיבוד",
      vc_recording_capture_faults_total: "תקלות לכידה",
      vc_scratch_store_free_ratio: "אחסון זמני פנוי",
      vc_recording_store_free_ratio: "אחסון הקלטות פנוי",
      vc_cpu_headroom_ratio: "עתודת מעבד"
    } as Record<string, string>
  },
  // Admin recordings surface
  rec: {
    search: "חיפוש (מערכת / אסמכתא / ת״ז / שם קובץ)",
    statusAll: "כל הסטטוסים",
    statusCompleted: "הושלם",
    statusQueued: "בתור",
    statusCompositing: "בעיבוד",
    statusCapturing: "בהקלטה",
    statusFailed: "נכשל",
    refresh: "רענון",
    none: "לא נמצאו הקלטות.",
    colStatus: "סטטוס",
    colCall: "שיחה",
    colCase: "מערכת / תיק / נהג",
    colDuration: "משך",
    colSize: "גודל",
    colCreated: "נוצר",
    colCompleted: "הושלם",
    play: "נגן",
    download: "הורדה",
    manifest: "מניפסט",
    signature: "חתימה",
    details: "פרטי הקלטה",
    notPlayable: "הקלטה זו אינה זמינה לצפייה (טרם הושלמה או הקובץ חסר).",
    seconds: "שנ׳",
    close: "סגור",
    loading: "טוען פרטי הקלטה…",
    selectHint: "בחר הקלטה מהרשימה כדי לצפות בה ולהורידה.",
    authTitle: "כניסה לצפייה בהקלטה",
    authHint: "נדרש שם משתמש וסיסמה נוספים רק להפעלת נגן ההקלטה.",
    username: "שם משתמש",
    password: "סיסמה",
    authSubmit: "כניסה",
    authChecking: "בודק…",
    authFailed: "שם המשתמש או הסיסמה אינם תקינים.",
    authExpired: "הרשאת הצפייה בהקלטה פגה. יש להיכנס שוב."
  },
  // Errors (mapped from VC-* codes — see design/10 §5)
  errors: {
    "VC-AUTH-001": "ההתחברות פגה — יש להיכנס מחדש.",
    "VC-AUTH-002": "אין לך הרשאה לפעולה זו.",
    "VC-AUTH-010": "שם המשתמש או הסיסמה שגויים.",
    "VC-AUTH-011": "בוצעו יותר מדי ניסיונות כושלים. יש להמתין מספר דקות ולנסות שוב.",
    "VC-AUTH-016": "הכניסה אינה זמינה כרגע. יש לנסות שוב או לפנות לתמיכה.",
    "VC-LAUNCH-001": "פרטי הפתיחה חסרים או שגויים — נא לפנות למערכת המארחת.",
    "VC-CALL-001": "הקצין אינו זמין כעת.",
    "VC-CALL-002": "אין מענה — ניתן לנסות שוב או לבחור קצין אחר.",
    "VC-CALL-003": "הקצין דחה את השיחה.",
    "VC-CALL-004": "המערכת בעומס מלא — נא לנסות שוב בעוד מספר דקות.",
    "VC-MEDIA-001": "נדרשת גישה למצלמה ולמיקרופון.",
    "VC-MEDIA-002": "תקלת מדיה — מתבצע חיבור מחדש…",
    "VC-MEDIA-003": "החיבור נכשל — השיחה הסתיימה. ההקלטה נשמרה.",
    "VC-MEDIA-004": "נדרש חיבור מאובטח (HTTPS) כדי לגשת למצלמה ולמיקרופון — יש לפתוח את ההדגמה בכתובת מאובטחת.",
    "VC-REC-001": "תקלה בהקלטה — נא לפנות לתמיכה.",
    "VC-CFG-001": "ערך תצורה אינו תקין.",
    "VC-SCHED-001": "פרטי הפגישה אינם תקינים — נא לבדוק את המשך והמשתתפים.",
    "VC-SCHED-002": "לא ניתן לבטל פגישה שהסתיימה.",
    "VC-SCHED-404": "הפגישה לא נמצאה.",
    "VC-RESYNC": "החיבור התחדש — מסנכרן מחדש את מצב השיחה…",
    "VC-SYS-001": "אירעה תקלה — נא לנסות שוב או לפנות לתמיכה."
  }
} as const;

export type ErrorCode = keyof typeof he.errors;

export function messageFor(code: string): string {
  return messageForFrom(he, code);
}

export function messageForFrom(strings: { errors: Record<string, string> }, code: string): string {
  return strings.errors[code] ?? strings.errors["VC-SYS-001"] ?? "An error occurred.";
}
